import moment from 'moment';

const AT_FAULT_REASON = 'At fault accident';

interface IAccidentClaimLike {
  date?: string;
  reason?: string;
}

interface IAdditionalDriverLike {
  accidentHistory?: boolean;
  accidentClaims?: IAccidentClaimLike[];
}

/**
 * Summary shown in the "Secondary driver accidents" row of the market scan
 * results details panel. Returns null when there is no secondary driver, which
 * hides the row (PolicyDetails only renders truthy values).
 */
export const getSecondaryDriverAccidentsLabel = (
  additionalDrivers?: IAdditionalDriverLike[],
): string | null => {
  const driver = additionalDrivers?.[0];
  if (!driver) return null;

  if (!driver.accidentHistory) return 'None';

  const claimCount = driver.accidentClaims?.length || 0;
  if (!claimCount) return 'Yes';

  return `${claimCount} claim${claimCount > 1 ? 's' : ''}`;
};

/**
 * Whether a scan lists any secondary driver. Used as a tracking-event property,
 * so it is always a real boolean (never undefined) to keep the analytics schema
 * consistent.
 */
export const hasAdditionalDrivers = (
  additionalDrivers?: IAdditionalDriverLike[],
): boolean => (additionalDrivers || []).length > 0;

/**
 * Total at-fault accidents within the last `withinYears` years across all
 * secondary drivers on a scan.
 *
 * Unlike the main driver's summary fields, these claims carry their own
 * `reason`, so not-at-fault ones are excluded here. Dates are ISO timestamps
 * and parsed with moment's default parser.
 */
export const getAdditionalDriversAtFaultCount = (
  additionalDrivers?: IAdditionalDriverLike[],
  withinYears = 3,
): number =>
  (additionalDrivers || []).reduce((total, driver) => {
    const recentAtFault = (driver?.accidentClaims || []).filter(
      (claim) =>
        claim?.reason === AT_FAULT_REASON &&
        !!claim?.date &&
        moment().diff(moment(claim.date), 'years') <= withinYears,
    );
    return total + recentAtFault.length;
  }, 0);
